import type { CustomMbscCalendarEventData, LocalVisitRequest } from "~/utils/eventsHelper";
import ShiftEvent from "./ShiftEvent";
import CalendarBlockBlocking from "./CalendarBlockBlocking";
import CalendarBlockFiltering from "./CalendarBlockFiltering";
import type { OrgConfig } from "~/providers/OrgConfigProvider";
import { renderScheduleEvent } from "./VisitEvent";
import type { VisitRenderType } from "./VisitEvent";
import { VisitEventStatusEnum } from "~/graphql/__generated__/graphql";
import type { CommandIndexProvidersQuery } from "~/graphql/__generated__/graphql";
import { UNASSIGNED_PROVIDER_ID } from "~/utils/visitsHelper";
import type { LocalCalendarBlock, LocalShift, LocalVisitResource } from "~/hooks/visitDragDropReducer";

export type CalendarEventRenderLookups = {
  visitResourceById: Map<number, LocalVisitResource>;
  visitRequestById: Map<number, LocalVisitRequest>;
  shiftById: Map<number, LocalShift>;
  calendarBlockById: Map<number, LocalCalendarBlock>;
};

type RenderCalendarEventOptions = {
  date: Date;
  viewMode: "provider" | "patient";
  providerLookup: Record<number, CommandIndexProvidersQuery["commandIndexProviders"]["providers"][number]>;
  utilizationPerShift: Record<string, number>;
  lookups: CalendarEventRenderLookups;
  orgConfig: OrgConfig;
};

const renderCalendarEvent = (event: CustomMbscCalendarEventData, {
  date,
  viewMode,
  providerLookup,
  utilizationPerShift,
  lookups,
  orgConfig,
}: RenderCalendarEventOptions) => {
  const original = event.original as unknown as CustomMbscCalendarEventData;
  if (!original) {
    console.error(`Missing original event. Not rendering anything`, event);
    return null;
  }

  if (original.type === "shift") {
    const shift = lookups.shiftById.get(original.referenceId);
    if (!shift) {
      console.error(`Invalid event. Could not find shift. Not rendering anything`, event);
      return null;
    }
    const shiftCenter = shift.shiftCenterId ? orgConfig["shiftCenters"][shift.shiftCenterId] : undefined
    const travel = shift.providerTravel === "inHomeOnly"
      ? "In-Home Only"
      : shift.providerTravel === "virtualOnly" ? "Virtual Only" : "In-Home or Virtual";

    return <ShiftEvent
      date={date}
      shift={shift}
      titleContent={travel}
      utilization={utilizationPerShift[`${shift.id}`]}
      centerpointName={shiftCenter?.name}
      overflowShift={shift.overflowShift}
    />
  } else if (original.type === "calendar_block") {
    const calendarBlock = lookups.calendarBlockById.get(original.referenceId);
    if (!calendarBlock) {
      console.error(`Invalid event. Could not find calendar block. Not rendering anything`, event);
      return null;
    }
    return <CalendarBlockBlocking date={date} calendarBlock={calendarBlock} eventStatus={calendarBlock.eventStatus} />
  } else if (original.type === "calendar_block_filter") {
    const calendarBlock = lookups.calendarBlockById.get(original.referenceId);
    if (!calendarBlock) {
      console.error(`Invalid event. Could not find calendar block. Not rendering anything`, event);
      return null;
    }
    return <CalendarBlockFiltering date={date} calendarBlock={calendarBlock} eventStatus={calendarBlock.eventStatus} />
  } else if (original.type === "visit_resource") {
    const visitResource = lookups.visitResourceById.get(original.referenceId);
    if (!visitResource) {
      console.error(`Invalid event. Could not find visit resource. Not rendering anything`, event);
      return null;
    }
    const visitType = orgConfig["visitTypes"][visitResource?.visit?.visitTypeId]
    const errandType = visitResource.visitResourceRequirementId
      ? orgConfig["errandTypeByRequirementId"][visitResource.visitResourceRequirementId]
      : undefined;
    const providerFromLookup = providerLookup[visitResource?.providerId ?? UNASSIGNED_PROVIDER_ID];
    const providerName = providerFromLookup
      ? `${providerFromLookup.firstName ?? ""} ${providerFromLookup.lastName ?? ""}`.trim()
      : visitResource.provider
        ? `${visitResource.provider.firstName ?? ""} ${visitResource.provider.lastName ?? ""}`.trim()
        : undefined;
    const isLocked = original.cssClass?.includes("optimize-review-locked") ?? false;

    const data: VisitRenderType = {
      isRequest: false,
      badState: !!original.tooltip,
      visitType: visitType || { name: "Visit" },
      errandType,
      errandStatus: visitResource.errandStatus,
      visit: visitResource.visit,
      patient: visitResource.visit.patient,
      providerId: visitResource.providerId,
      virtualProviderStatus: visitResource.virtualProviderStatus,
      providerName,
      viewMode,
      locked: isLocked,
      precautionIds: visitResource.visit.episode?.precautions?.map((p) => p.precautionId) ?? [],
    }
    return renderScheduleEvent(original, data);
  } else if (original.type === "visit_request") {
    // This only happens while the visit request is being dragged and hasn't been dropped
    const visitRequest = lookups.visitRequestById.get(original.referenceId);
    if (!visitRequest) {
      console.error(`Invalid event. Could not find visit request. Not rendering anything`, event);
      return null;
    }

    const data: VisitRenderType = {
      isRequest: true,
      badState: false,
      visitType: visitRequest.visitType || { name: "Visit" },
      visit: { eventStatus: VisitEventStatusEnum.Scheduled, id: -1 },
      patient: visitRequest.patient,
      viewMode,
    }
    return renderScheduleEvent(original, data);
  }

  console.error(`Unknown event type: ${original.type}. Not rendering anything`, event);
  return null; // Handle other types if necessary
};

export default renderCalendarEvent;
